import { SynthesisStep } from "@/types/chemistry";
import { Check, Layers } from "lucide-react";
import ChemicalPreview from "./ChemicalPreview";
import ReactionStructure from "@/components/step/chemistry/ReactionStructure";
import { getStepDisplayName } from "@/lib/stepUtils";

const mdlRxnFile = `$RXN V3000

 -INDIGO- 0226262156

M  V30 COUNTS 2 1
M  V30 BEGIN REACTANT
M  V30 BEGIN CTAB
M  V30 COUNTS 7 7 0 0 0
M  V30 BEGIN ATOM
M  V30 1 C 11.1156 -9.77147 0.0 0
M  V30 2 C 11.1156 -11.5035 0.0 0
M  V30 3 C 11.6156 -10.6375 0.0 0
M  V30 4 C 10.1156 -11.5035 0.0 0
M  V30 5 C 10.1156 -9.77147 0.0 0
M  V30 6 C 9.61563 -10.6375 0.0 0
M  V30 7 Br 12.6156 -10.6375 0.0 0
M  V30 END ATOM
M  V30 BEGIN BOND
M  V30 1 2 3 1
M  V30 2 1 1 5
M  V30 3 2 5 6
M  V30 4 1 6 4
M  V30 5 2 4 2
M  V30 6 1 2 3
M  V30 7 1 3 7
M  V30 END BOND
M  V30 END CTAB
M  V30 BEGIN CTAB
M  V30 COUNTS 9 9 0 0 0
M  V30 BEGIN ATOM
M  V30 1 C 15.8431 -9.77147 0.0 0
M  V30 2 C 15.8431 -11.5035 0.0 0
M  V30 3 C 16.3431 -10.6375 0.0 0
M  V30 4 C 14.8431 -11.5035 0.0 0
M  V30 5 C 14.8431 -9.77147 0.0 0
M  V30 6 C 14.3431 -10.6375 0.0 0
M  V30 7 C 17.3431 -10.6375 0.0 0
M  V30 8 O 17.8431 -11.5035 0.0 0
M  V30 9 O 17.8431 -9.77147 0.0 0
M  V30 END ATOM
M  V30 BEGIN BOND
M  V30 1 2 3 1
M  V30 2 1 1 5
M  V30 3 2 5 6
M  V30 4 1 6 4
M  V30 5 2 4 2
M  V30 6 1 2 3
M  V30 7 1 3 7
M  V30 8 1 7 8
M  V30 9 2 7 9
M  V30 END BOND
M  V30 END CTAB
M  V30 END REACTANT
M  V30 BEGIN PRODUCT
M  V30 BEGIN CTAB
M  V30 COUNTS 12 13 0 0 0
M  V30 BEGIN ATOM
M  V30 1 C 21.6844 -9.77147 0.0 0
M  V30 2 C 21.6844 -11.5035 0.0 0
M  V30 3 C 22.1844 -10.6375 0.0 0
M  V30 4 C 20.6844 -11.5035 0.0 0
M  V30 5 C 20.6844 -9.77147 0.0 0
M  V30 6 C 20.1844 -10.6375 0.0 0
M  V30 7 C 24.6844 -9.77147 0.0 0
M  V30 8 C 24.6844 -11.5035 0.0 0
M  V30 9 C 25.1844 -10.6375 0.0 0
M  V30 10 C 23.6844 -11.5035 0.0 0
M  V30 11 C 23.6844 -9.77147 0.0 0
M  V30 12 C 23.1844 -10.6375 0.0 0
M  V30 END ATOM
M  V30 BEGIN BOND
M  V30 1 2 3 1
M  V30 2 1 1 5
M  V30 3 2 5 6
M  V30 4 1 6 4
M  V30 5 2 4 2
M  V30 6 1 2 3
M  V30 7 2 9 7
M  V30 8 1 7 11
M  V30 9 2 11 12
M  V30 10 1 12 10
M  V30 11 2 10 8
M  V30 12 1 8 9
M  V30 13 1 12 3
M  V30 END BOND
M  V30 END CTAB
M  V30 END PRODUCT
M  END
`.trim();

const StepCard = ({
    step,
    onOpen,
    onToggleComplete,
    selecting,
    selected,
    zoom = 1,
    isTarget,
}: {
    step: SynthesisStep;
    onOpen: () => void;
    onToggleComplete: () => void;
    selecting?: boolean;
    selected?: boolean;
    zoom?: number;
    isTarget?: boolean;
}) => {
    const compoundName = isTarget ? (step.products?.[0]?.name || "") : "";

    return (
        <div className="flex flex-col items-start" data-step-card onClick={onOpen}>
            <div className="flex items-center gap-2 mb-1.5 w-[280px] justify-between">
                <div className="flex items-center gap-1.5" style={{ fontSize: `${14 / zoom}px` }}>
                    {isTarget && compoundName ? (
                        <span className="bg-primary/10 text-primary font-medium rounded px-1.5 py-0.5" style={{ fontSize: 'inherit' }}>{compoundName}</span>
                    ) : (
                        <span className="text-muted-foreground font-medium" style={{ fontSize: 'inherit' }}>{getStepDisplayName(step)}</span>
                    )}
                    {!isTarget && (step.variants?.length ?? 0) > 1 && (
                        <div className="flex items-center gap-0.5 bg-primary/10 text-primary rounded px-1.5 py-0.5" style={{ fontSize: `${10 / zoom}px` }}>
                            <Layers style={{ width: `${12 / zoom}px`, height: `${12 / zoom}px` }} />
                            <span className="font-semibold">{step.variants?.length ?? 0}</span>
                        </div>
                    )}
                </div>
                {selecting && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onOpen(); }}
                        className={`rounded-full border flex items-center justify-center transition-colors ${selected
                                ? "bg-primary border-primary text-primary-foreground"
                                : "border-muted-foreground/40 hover:border-primary"
                            }`}
                        style={{ width: `${20 / zoom}px`, height: `${20 / zoom}px` }}
                    >
                        {selected && <Check style={{ width: `${12 / zoom}px`, height: `${12 / zoom}px` }} />}
                    </button>
                )}
            </div>
            <div
                data-step-card
                className={`w-[280px] h-[180px] rounded-xl border bg-card flex items-center justify-center relative cursor-pointer hover:border-primary/30 transition-all overflow-hidden ${
                    selecting && selected ? "border-primary ring-1 ring-primary/30" : "border-border/50"
                }`}
            >
                <ReactionStructure reactionString={step.reactionString ?? mdlRxnFile} width={280} height={180} />
            </div>
        </div>
    );
};

export default StepCard;